import { ChangeDetectionStrategy, Component, computed, inject, input, linkedSignal, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import { catchError, map, of, startWith, switchMap } from 'rxjs';
import {
  EmailService,
  EmailType,
  type EmailLogEntry,
  type Paginated,
  ReviewAudience,
} from '@brafton/feedback-api';

/** A log entry resolved for display. */
interface EmailLogRow {
  id: string;
  sentAt: string;
  title: string;
  sender: string;
  summary: string;
  recipients: string[];
  rejected: { email: string; reason: string }[];
}

const PAGE_SIZE = 10;

/**
 * The project's sent-email log, below the feedback list: every manual email and every
 * daily email that included this project — when, who sent it (or that it was
 * automatic), the type, who received it, and anyone who didn't and why.
 *
 * Fetches its own data. Bump `refreshKey` after sending an email to pull the new entry.
 */
@Component({
  selector: 'app-email-log-panel',
  imports: [DatePipe],
  templateUrl: './email-log-panel.html',
  styleUrl: './email-log-panel.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmailLogPanel {
  private readonly emails = inject(EmailService);

  /** The project (feedback round) whose log to show. */
  readonly feedbackRoundId = input('');

  /** Change it to refetch (e.g. after an email is sent). */
  readonly refreshKey = input(0);

  /** How many pages of the log are shown; resets when the project or refresh changes. */
  private readonly pagesShown = linkedSignal(() => {
    this.feedbackRoundId();
    this.refreshKey();
    return 1;
  });

  /** Entries whose recipient list is expanded. */
  protected readonly expanded = signal<ReadonlySet<string>>(new Set());

  private readonly logState = toSignal(
    toObservable(computed(() => ({ roundId: this.feedbackRoundId(), pages: this.pagesShown() }))).pipe(
      switchMap(({ roundId, pages }) =>
        roundId
          ? this.emails.listEmails(roundId, 1, PAGE_SIZE * pages).pipe(
              map((page) => ({ loading: false, page })),
              catchError(() => of({ loading: false, page: null as Paginated<EmailLogEntry> | null })),
              startWith({ loading: true, page: null as Paginated<EmailLogEntry> | null }),
            )
          : of({ loading: false, page: null as Paginated<EmailLogEntry> | null }),
      ),
    ),
    { initialValue: { loading: true, page: null as Paginated<EmailLogEntry> | null } },
  );

  protected readonly loading = computed(() => this.logState().loading);
  protected readonly total = computed(() => this.logState().page?.total ?? 0);
  protected readonly hasMore = computed(() => (this.logState().page?.results.length ?? 0) < this.total());

  protected readonly rows = computed<EmailLogRow[]>(() =>
    (this.logState().page?.results ?? []).map((entry) => ({
      id: entry.id,
      sentAt: entry.sentAt,
      title: this.titleFor(entry),
      sender: entry.sentBy?.name || entry.sentBy?.email || 'Automatic',
      summary: this.summaryFor(entry),
      recipients: entry.recipients ?? [],
      rejected: (entry.rejected ?? []).map((rejection) => ({ email: rejection.email, reason: rejection.reasonLabel })),
    })),
  );

  protected loadMore(): void {
    this.pagesShown.update((pages) => pages + 1);
  }

  protected toggle(entryId: string): void {
    this.expanded.update((expandedIds) => {
      const next = new Set(expandedIds);
      if (next.has(entryId)) {
        next.delete(entryId);
      } else {
        next.add(entryId);
      }
      return next;
    });
  }

  private titleFor(entry: EmailLogEntry): string {
    if (entry.type === EmailType.DailyDigest) {
      return 'Daily email';
    }
    const audience = entry.reviewAudience === ReviewAudience.Internal ? 'Internal' : 'Client';
    return `Pages Ready for Review — ${audience}`;
  }

  private summaryFor(entry: EmailLogEntry): string {
    const summary = entry.summary ?? {};
    if (entry.type === EmailType.PagesReadyForReview) {
      const pageCount = summary.pages?.length ?? 0;
      return `${pageCount} ${pageCount === 1 ? 'page' : 'pages'} listed`;
    }

    const parts = [
      [summary.newForAction, 'new for action'],
      [summary.markedForRevise, 'marked for revise'],
      [summary.forReview, 'for review'],
      [summary.needingAdditionalInformation, 'need more information'],
      [summary.revisedPages?.length, summary.revisedPages?.length === 1 ? 'page marked for revise' : 'pages marked for revise'],
    ]
      .filter(([count]) => typeof count === 'number' && count > 0)
      .map(([count, label]) => `${count} ${label}`);

    return parts.join(' · ');
  }
}
